import DeviceService from "../../DeviceService";
import {compileWhereToFn, validateUrl} from "../utils";
import { IBaseGameConfig } from "../types/IGameLoader.types";

export const queryFunctions: {
    [index: string]: (
        args?: { [index: string]: string },
        params?: Partial<IBaseGameConfig>,
        key?: string,
        mutableParams?: Partial<IBaseGameConfig>,
        localParams?: Partial<IBaseGameConfig>,
    ) => string | undefined;
} = {
    /**
     * Auto detect device type with configured result values
     * @param args
     */
    device_auto_detect: (args): string | undefined => {
        if (!args || args["arg_as_mobile"] === undefined) {
            console.warn(
                `Query function "device_auto_detect" needs variable as: <query... arg_as_mobile="mob" />`
            );
            return undefined;
        }

        if (!args || args["arg_as_desktop"] === undefined) {
            console.warn(
                `Query function "device_auto_detect" needs variable as: <query... arg_as_desktop="desk" />`
            );
            return undefined;
        }

        return DeviceService.isMobile()
            ? args["arg_as_mobile"]
            : args["arg_as_desktop"];
    },

    /**
     * Set value if WHERE give TRUE (Where like SQL query)
     *
     * @examples.arg_key - `logo` using if we need to change exit value
     *
     * @examples.arg_where - `currency = 'EUR'`
     * @examples.arg_where - `currency != 'EUR'`
     *
     * @examples.arg_where - `currency IN ('EUR', 'USD')`
     * @examples.arg_where - `currency NOT IN ('EUR', 'USD')`
     *
     * @examples.arg_where - `currency = 'EUR' AND lang = 'en'`
     * @examples.arg_where - `currency = 'EUR' OR lang = 'en'`
     *
     * @examples.arg_where - `(currency = 'EUR' AND lang = 'en') OR game_name = '3_coins'`
     *
     * @param args {{arg_where?: string; arg_key?: string; arg_value?: string; arg_debug?: string;}}
     * @param params {Object}
     * @param key {string}
     * @param mutableParams
     */
    set_value_if_where: (
        args?: { arg_where?: string; arg_key?: string; arg_value?: string; arg_debug?: string },
        params?: Partial<IBaseGameConfig>,
        key?: string,
        mutableParams?: Partial<IBaseGameConfig>,
    ): undefined => {
        if (!args || args["arg_where"] === undefined) {
            console.warn(
                `Query function "change_value_by_where" needs variable as: <query... arg_where="..." />`
            );
            return undefined;
        }

        if (!args || args["arg_value"] === undefined) {
            console.warn(
                `Query function "change_value_by_where" needs variable as: <query... arg_value="some_value" />`
            );
            return undefined;
        }

        if (!params) {
            console.warn(
                `Query function "change_value_by_where" needs bash params!`
            );
            return undefined;
        }

        const debugMode = args["arg_debug"] === "1";

        if (debugMode) {
            console.group(`[Bash][Expression][Debug] Key: "${key}"`);
            console.log("Params:", params);
            console.log("Raw Where:", `"${args["arg_where"]}"`);
        }

        try {
            if (!compileWhereToFn(args["arg_where"], debugMode)(params)) {
                if (debugMode) {
                    console.log(
                        `Result not match; Value: "${
                            (params as any)[key as any]
                        }"`
                    );
                }
                return undefined;
            }

            if (debugMode) {
                console.log(`Result matched; Value: "${args["arg_value"]}"`);
            }

            const customKey = String(args['arg_key'] || key);
            (mutableParams as any)[customKey] = args["arg_value"];

            return undefined;
        } catch (e: any) {
            console.warn(
                `[Bash][Exp] Key: "${key}"; Error: ${e.message}`,
                e.stack
            );
            return undefined;
        } finally {
            if (debugMode) {
                console.groupEnd();
            }
        }
    },

    /**
     * Override domain name in parameters values
     *
     * @examples.arg_keys - `server_url,replay_url`
     * @examples.arg_with_protocol - `true | false` - using for rewrite protocol (optional)
     * @examples.arg_keep_key - `keep` # keep and use key for game launcher
     *
     * @param args
     * @param params
     * @param key
     * @param mutableParams
     * @param localParams
     */
    override_domains_by_key: (
        args?: { arg_keys?: string; arg_keep_key?: string; arg_with_protocol?: string; },
        params?: Partial<IBaseGameConfig>,
        key?: string,
        mutableParams?: Partial<IBaseGameConfig>,
        localParams?: Partial<IBaseGameConfig>,
    ): undefined => {
        if (!args || args["arg_keys"] === undefined) {
            console.warn(
                `Query function "override_domains_by_key" needs variable as: <query... arg_keys="server_url,replay_url,..." arg_with_protocol="false" arg_keep_key="keep"  />`
            );
            return;
        }

        if (!params) {
            console.warn(
                `Query function "override_domains_by_key" needs bash params!`
            );
            return;
        }

        if (key === undefined) {
            return;
        }

        const domainUrl = (params as any)[key];
        if (!domainUrl) {
            return;
        }

        const newDomainUrl = validateUrl(domainUrl, `Query function "override_domains_by_key" catch error: Url "${(params as any)[key]}" in parameter "${key}" is not valid!`, false);
        if (!newDomainUrl) {
            return;
        }

        const customKeys = String(args["arg_keys"])
            ?.split(',')
            .map(e => e.trim())
            .filter(e => !!e);

        if (customKeys.length === 0) {
            return;
        }

        const changeByReference = (mutableObject: any, customKey: string): void => {
            if (!mutableObject || !mutableObject[customKey]) {
                return;
            }

            const url = validateUrl(mutableObject[customKey], `Query function "override_domains_by_key" catch error: Url "${mutableObject[customKey]}" is not URL!`, false);
            if (!url) {
                // check if URL without protocol, but need to change domain
                if (
                    /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/.*)?$/.test(
                        mutableObject[customKey]
                    )
                ) {
                    mutableObject[customKey] = mutableObject[customKey].replace(
                        /^[^\/]+/,
                        newDomainUrl.host
                    );
                }
                return;
            }

            // option for override URL protocol
            if (args["arg_with_protocol"] === "true") {
                url.protocol = newDomainUrl.protocol;
            }

            url.host = newDomainUrl.host;

            mutableObject[customKey] = url.toString();
        }

        for (let i = 0; i < customKeys.length; i++) {
            const customKey = customKeys[i];

            changeByReference(mutableParams, customKey);
            changeByReference(localParams, customKey);
        }

        if (args["arg_keep_key"] === undefined || args["arg_keep_key"] !== "keep") {
            delete (mutableParams as any)[key];
        }

        return;
    },

    /**
     * Set variable and use value from exist key
     * @example.arg_key - 'wl' create new key "wl" with value "partner" >> {[wl]: partner}
     * @examples.arg_from_key - `partner` using a value of this key
     *
     * @param args
     * @param params
     * @param key
     * @param mutableParams
     * @param localParams
     */
    set_value_from_key: (
        args?: { arg_key?: keyof IBaseGameConfig; arg_from_key?: keyof IBaseGameConfig},
        params?: Partial<IBaseGameConfig>,
        key?: string,
        mutableParams?: Partial<IBaseGameConfig>,
        localParams?: Partial<IBaseGameConfig>,
    ): undefined => {
        if (
            !args || args["arg_key"] === undefined || !args["arg_key"]?.trim()
            || args["arg_from_key"] === undefined || !args["arg_from_key"]?.trim()) {
            console.warn(
                `Query function "set_value_from_key" needs variable as: <query... arg_key="wl" arg_from_key="partner" />`
            );
            return;
        }

        if (!params) {
            console.warn(
                `Query function "set_value_from_key" needs bash params!`
            );
            return;
        }

        if (key === undefined) {
            console.warn(
                `Query function "set_value_from_key" needs attribute "key"!`
            );
            return;
        }

        const data: Partial<IBaseGameConfig> = {
            ...params,
            ...localParams,
        };

        const keyValue = data[args["arg_from_key"]];
        if (!keyValue) {
            return;
        }

        (localParams as any)[args["arg_key"]] = keyValue;
        (mutableParams as any)[args["arg_key"]] = keyValue;
    }
};
